import { ApolloClient, gql } from 'apollo-boost';
import { InvestmentSplitWithAnswers } from './utils';
import { Task } from '../api';
import { removeDollarSign } from 'shared/design-system/components/TrueImpactDollarField';
import { logError } from 'shared/utils/logError';
import { AllocationReports, AllocationTask } from './allocationLocalStorage';
import {
  AllocationAnswer,
  getInvestmentType,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';

interface AllocationRequest {
  allocations: {
    investmentId: number;
    taskId: number;
    amount: number;
    programId: number;
    reportId: number | undefined;
    year: number;
    allocationAnswers: AllocationAnswer[];
    tier: string;
    token?: string;
  }[];
}

const mutation = gql`
  mutation allocationRequest($allocations: [AllocationRequestInput]!) {
    allocationRequest(allocations: $allocations) {
      reportStatusList {
        reportId
        new
      }
      success
      error
    }
  }
`;

const mapInvestmentToAllocation = (
  task: Task,
  investments: Required<InvestmentSplitWithAnswers>[],
  reports: AllocationReports,
  token?: string,
  isImpactSnapshot?: boolean,
): AllocationRequest['allocations'] => {
  const result: AllocationRequest['allocations'] = [];
  investments.forEach(i => {
    // A task id is not an investment id: without a real one, skip rather than
    // allocate against whatever that number happens to point at.
    const investmentId =
      task.invitation?.investment?.dbId || task.allocation?.investment?.dbId;
    if (!investmentId) {
      logError('Unable to add investment', task);
      return;
    }
    const reportId = reports[i.program.name]?.[i.year];
    const allocationAnswers: AllocationAnswer[] = i.answers.map(a => ({
      questionId: a.question.questionId,
      questionText: a.question.questionText,
      text: a.answer,
      ignoreClearData: true,
    }));
    const tier = isImpactSnapshot ? 'SIGNIFICANT' : getInvestmentType(allocationAnswers).toUpperCase();
    result.push({
      reportId,
      tier,
      amount: Number(removeDollarSign(i.amount)),
      investmentId: investmentId,
      programId: i.program.Id,
      year: Number(i.year),
      taskId: task.taskId,
      allocationAnswers: allocationAnswers,
      ...(token && { token }),
    });
  });
  return result;
};

export interface AllocationRequestResponse {
  allocationRequest: {
    reportStatusList: {
      reportId: number;
      new: boolean;
    }[];
    success: boolean;
    error?: string;
  };
}

export const allocationRequest = (
  client: ApolloClient<object>,
  task: Task,
  investments: Required<InvestmentSplitWithAnswers>[],
  reports: AllocationReports,
  token?: string,
  isImpactSnapshot?: boolean,
) => {
  const variables = {
    allocations: mapInvestmentToAllocation(task, investments, reports, token, isImpactSnapshot),
  };

  return client.mutate<AllocationRequestResponse>({
    mutation,
    variables,
    fetchPolicy: 'no-cache',
  });
};

const allocationWizardProgressQuery = gql`
  query allocationWizardProgress($investmentId: Int!) {
    allocationWizardProgress(investmentId: $investmentId) {
      data
    }
  }
`;

const setAllocationWizardProgressMutation = gql`
  mutation setAllocationWizardProgress($investmentId: Int!, $data: String!) {
    setAllocationWizardProgress(investmentId: $investmentId, data: $data) {
      success
    }
  }
`;

const deleteAllocationWizardProgressMutation = gql`
  mutation deleteAllocationWizardProgress($investmentId: Int!) {
    deleteAllocationWizardProgress(investmentId: $investmentId) {
      success
    }
  }
`;

export interface AllocationWizardProgressQueryResponse {
  allocationWizardProgress: {
    data: string | null;
  } | null;
}

export const getAllocationWizardProgress = (
  client: ApolloClient<object>,
  investmentId: number,
) =>
  client.query<AllocationWizardProgressQueryResponse>({
    query: allocationWizardProgressQuery,
    variables: { investmentId },
    fetchPolicy: 'no-cache',
  });

export interface AllocationWizardProgressMutationResponse {
  setAllocationWizardProgress: {
    success: boolean;
  };
}

export const setAllocationWizardProgress = (
  client: ApolloClient<object>,
  investmentId: number,
  data: AllocationTask,
) =>
  client.mutate<AllocationWizardProgressMutationResponse>({
    mutation: setAllocationWizardProgressMutation,
    variables: {
      investmentId,
      data: JSON.stringify(data),
    },
    fetchPolicy: 'no-cache',
  });

export interface DeleteAllocationWizardProgressMutationResponse {
  deleteAllocationWizardProgress: {
    success: boolean;
  };
}

export const deleteAllocationWizardProgress = (
  client: ApolloClient<object>,
  investmentId: number,
) =>
  client.mutate<DeleteAllocationWizardProgressMutationResponse>({
    mutation: deleteAllocationWizardProgressMutation,
    variables: { investmentId },
    fetchPolicy: 'no-cache',
  });
